<template lang="pug">
v-dialog(
  v-model="showTutorial"
  persistent
  max-width="600"
  :fullscreen="$vuetify.breakpoint.xsOnly"
)
  v-card
    v-toolbar(color="primary" dark)
      v-toolbar-title Guía de Marketing Digital
      v-spacer
      v-btn(icon @click="completeTutorial")
        v-icon mdi-close
    
    v-window(v-model="step")
      v-window-item(v-for="n in 6" :key="`step-${n}`" :value="n")
        v-card-text.pa-5
          div(v-if="n === 1")
            .text-h5.mb-4 ¡Bienvenido a la herramienta de marketing!
            .text-body-1.mb-4 Ahora podés medir con precisión el rendimiento de tus campañas de publicidad digital. Sabrás exactamente cuántas órdenes y reservas generan tus anuncios en Google, Instagram, Facebook y otros canales.
            v-img.my-5(
              src="require('@/assets/marketing-intro.jpg')"
              alt="Marketing digital para restaurantes"
              contain
              max-height="200"
            )
            .text-body-1 ¿Querés conocer las principales funciones?
          
          div(v-if="n === 2")
            .text-h5.mb-4 Tres herramientas clave
            .d-flex.flex-column.mb-4
              v-card.mb-3.pa-3(outlined)
                .d-flex.align-center
                  v-avatar(color="primary" size="36" class="mr-3")
                    v-icon(dark) mdi-chart-line
                  div
                    .subtitle-1.font-weight-medium Reportes de Campañas
                    .body-2 Medí cuántas ventas generan tus anuncios
                
              v-card.mb-3.pa-3(outlined)
                .d-flex.align-center
                  v-avatar(color="primary" size="36" class="mr-3")
                    v-icon(dark) mdi-link-variant
                  div
                    .subtitle-1.font-weight-medium Generador de URLs
                    .body-2 Creá links especiales para tus campañas
              
              v-card.pa-3(outlined)
                .d-flex.align-center
                  v-avatar(color="primary" size="36" class="mr-3")
                    v-icon(dark) mdi-google-analytics
                  div
                    .subtitle-1.font-weight-medium Configuración de Analytics
                    .body-2 Conectá con Google Analytics y Facebook Pixel
          
          div(v-if="n === 3")
            .text-h5.mb-4 Tu primera URL de campaña
            .text-body-1.mb-3 Empecemos creando tu primera URL para una campaña:
            v-card.pa-3.mb-4(outlined)
              .subtitle-1.primary--text Ejemplo para Instagram
              v-list-item
                v-list-item-icon
                  v-icon mdi-web
                v-list-item-content
                  v-list-item-title URL base
                  v-list-item-subtitle tu-restaurante.restonline.com.ar
              v-list-item
                v-list-item-icon
                  v-icon mdi-source-branch
                v-list-item-content
                  v-list-item-title Fuente (UTM Source)
                  v-list-item-subtitle instagram
              v-list-item
                v-list-item-icon
                  v-icon mdi-vector-triangle
                v-list-item-content
                  v-list-item-title Medio (UTM Medium)
                  v-list-item-subtitle social
              v-list-item
                v-list-item-icon
                  v-icon mdi-tag
                v-list-item-content
                  v-list-item-title Campaña
                  v-list-item-subtitle verano2025
              v-list-item
                v-list-item-icon
                  v-icon mdi-content-copy
                v-list-item-content
                  v-list-item-title Contenido
                  v-list-item-subtitle historias
            .text-body-2.font-italic Esta URL te permitirá seguir todas las ventas que generan tus historias de Instagram.
          
          div(v-if="n === 4")
            .text-h5.mb-4 Analizando resultados
            .text-body-1.mb-2 Una vez que tus campañas estén activas, podrás ver:
            v-row.mb-4
              v-col(cols="6" sm="3")
                v-card.pa-2.text-center(outlined height="100%")
                  v-icon.mb-1(color="primary") mdi-shopping
                  .caption Órdenes por campaña
              v-col(cols="6" sm="3")
                v-card.pa-2.text-center(outlined height="100%")
                  v-icon.mb-1(color="primary") mdi-calendar-check
                  .caption Reservas por canal
              v-col(cols="6" sm="3")
                v-card.pa-2.text-center(outlined height="100%")
                  v-icon.mb-1(color="primary") mdi-cash-multiple
                  .caption Facturación total
              v-col(cols="6" sm="3")
                v-card.pa-2.text-center(outlined height="100%")
                  v-icon.mb-1(color="primary") mdi-cellphone
                  .caption Dispositivos usados
            v-card.pa-4(outlined)
              .text-caption.mb-2.font-italic Ejemplo de informe
              v-simple-table(dense)
                thead
                  tr
                    th Fuente
                    th Órdenes
                    th Reservas
                    th Facturación
                tbody
                  tr
                    td 
                      v-chip(x-small color="blue" dark) instagram
                    td 28
                    td 14
                    td $52.600
                  tr
                    td 
                      v-chip(x-small color="red" dark) google
                    td 35
                    td 7
                    td $71.850
            .text-body-2.mt-3 Filtrá por fechas y canales para encontrar tus mejores campañas.
          
          div(v-if="n === 5")
            .text-h5.mb-4 Configuración de Analytics
            .text-body-1.mb-3 Para aprovechar al máximo el sistema:
            v-stepper.mb-4(vertical)
              v-stepper-step(step="1" complete) Creá una cuenta en Google Analytics (gratuita)
              v-stepper-content(step="1")
                .body-2 Visitá 
                  a(href="https://analytics.google.com" target="_blank") analytics.google.com
                  |  para crear una cuenta gratuita
                
              v-stepper-step(step="2" complete) Copiá tu ID de medición (G-XXXXXXXX)
              v-stepper-content(step="2")
                .body-2 Lo encontrarás en Admin > Propiedad > Flujos de datos
                
              v-stepper-step(step="3" complete) Configuralo en Restonline
              v-stepper-content(step="3")
                .body-2 En la sección de Configuración de Analytics
            
            v-alert(dense text type="success" icon="mdi-check-circle")
              | ¡Listo! Ahora verás datos detallados de conversiones directamente en Google Analytics.
              
          div(v-if="n === 6")
            .text-h5.mb-4 Consejos para restaurantes cordobeses
            v-list
              v-list-item
                v-list-item-icon
                  v-icon(color="success") mdi-check-circle
                v-list-item-content
                  v-list-item-title Promocioná tus platos regionales con fotos de calidad
              v-list-item
                v-list-item-icon
                  v-icon(color="success") mdi-check-circle
                v-list-item-content
                  v-list-item-title Creá campañas específicas para fines de semana
              v-list-item
                v-list-item-icon
                  v-icon(color="success") mdi-check-circle
                v-list-item-content
                  v-list-item-title Usá diferentes URLs para Instagram, Facebook y Google
              v-list-item
                v-list-item-icon
                  v-icon(color="success") mdi-check-circle
                v-list-item-content
                  v-list-item-title Compará resultados entre delivery y reservas
              v-list-item
                v-list-item-icon
                  v-icon(color="success") mdi-check-circle
                v-list-item-content
                  v-list-item-title Promocioná eventos y fechas especiales con campañas dedicadas
            
            v-alert.mt-4(dense outlined type="info")
              | ¿Sabías? En Córdoba, las campañas en Instagram generan 30% más pedidos que otros canales durante los fines de semana.
          
    v-divider
    
    v-card-actions
      v-btn(text @click="skipTutorial") Saltar tutorial
      v-spacer
      v-btn(
        v-if="step > 1"
        text
        @click="step--"
      )
        v-icon(left) mdi-arrow-left
        | Anterior
      v-btn(
        v-if="step < 6"
        color="primary"
        @click="step++"
      )
        | Siguiente
        v-icon(right) mdi-arrow-right
      v-btn(
        v-else
        color="success"
        @click="completeTutorial"
      )
        v-icon(left) mdi-check
        | Completar
</template>

<script>
export default {
  name: 'MarketingTutorial',
  
  data() {
    return {
      showTutorial: true,
      step: 1
    };
  },
  
  created() {
    // Verificar si el usuario ya vio el tutorial
    const tutorialShown = localStorage.getItem('marketing_tutorial_shown');
    this.showTutorial = !tutorialShown;
  },
  
  methods: {
    skipTutorial() {
      this.showTutorial = false;
      localStorage.setItem('marketing_tutorial_shown', 'true');
    },
    
    completeTutorial() {
      this.showTutorial = false;
      localStorage.setItem('marketing_tutorial_shown', 'true');
      this.$toast.success('¡Tutorial completado! Podés volver a verlo desde la sección de Ayuda.');
    }
  }
};
</script>

<style scoped>
.v-window {
  /* Asegurarse de que el contenido del tutorial tenga altura suficiente en dispositivos móviles */
  min-height: 450px;
}

/* Estilos para dispositivos móviles */
@media (max-width: 600px) {
  .v-window {
    min-height: 520px;
  }
}
</style>
